import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActualServer,
  OverspendApplyTransfer,
  OverspendCoverageItem,
  OverspendSourceCategory,
  OverspendSuggestionResult,
  OverspendTransfer
} from '../../types';
import { applyOverspendCoverage, dismissOverspendCoverage, isRateLimitError, repromptOverspendCoverage } from '../../utils/overspendApi';
import { formatBudgetCurrency } from '../../utils/budgetFormat';

interface OverspendCoverageModalProps {
  isVisible: boolean;
  onClose: () => void;
  server: ActualServer;
  budgetId: string;
  budgetPassword?: string;
  currency?: string;
  suggestion: OverspendSuggestionResult | null;
  loading?: boolean;
  loadError?: string | null;
  onSuggestionChange?: (suggestion: OverspendSuggestionResult) => void;
}

function cloneCoverages(coverages: OverspendCoverageItem[]): OverspendCoverageItem[] {
  return coverages.map(coverage => ({
    ...coverage,
    transfers: coverage.transfers.map(transfer => ({ ...transfer }))
  }));
}

function computeUsedBySource(coverages: OverspendCoverageItem[]): Map<string, number> {
  const used = new Map<string, number>();
  for (const coverage of coverages) {
    for (const transfer of coverage.transfers) {
      used.set(
        transfer.fromCategoryId,
        (used.get(transfer.fromCategoryId) || 0) + transfer.amount
      );
    }
  }
  return used;
}

function getRemainingAmount(
  source: OverspendSourceCategory,
  usedBySource: Map<string, number>
): number {
  const used = usedBySource.get(source.categoryId) || 0;
  return Math.max(0, source.availableAmount - used);
}

function toApplyTransfers(coverages: OverspendCoverageItem[]): OverspendApplyTransfer[] {
  const transfers: OverspendApplyTransfer[] = [];
  for (const coverage of coverages) {
    for (const transfer of coverage.transfers) {
      if (transfer.amount > 0) {
        transfers.push({
          fromCategoryId: transfer.fromCategoryId,
          toCategoryId: coverage.overspentCategoryId,
          amount: transfer.amount
        });
      }
    }
  }
  return transfers;
}

const OverspendCoverageModal: React.FC<OverspendCoverageModalProps> = ({
  isVisible,
  onClose,
  server,
  budgetId,
  budgetPassword,
  currency = 'USD',
  suggestion,
  loading = false,
  loadError = null,
  onSuggestionChange
}) => {
  const { t } = useTranslation();
  const [manualMode, setManualMode] = useState(false);
  const [editedCoverages, setEditedCoverages] = useState<OverspendCoverageItem[]>([]);
  const [isApplying, setIsApplying] = useState(false);
  const [isReprompting, setIsReprompting] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [userFeedback, setUserFeedback] = useState('');
  const [applyError, setApplyError] = useState<string | null>(null);
  const [repromptError, setRepromptError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setManualMode(false);
      setApplyError(null);
      setRepromptError(null);
      setApplySuccess(false);
      setIsApplying(false);
      setIsReprompting(false);
      setIsDismissing(false);
      setUserFeedback('');
      return;
    }
    if (suggestion?.plan.coverages) {
      setEditedCoverages(cloneCoverages(suggestion.plan.coverages));
    }
  }, [isVisible, suggestion]);

  const availableSources = suggestion?.availableSources ?? [];
  const usedBySource = useMemo(() => computeUsedBySource(editedCoverages), [editedCoverages]);

  const totalToCover = useMemo(
    () => (suggestion?.overspentCategories ?? []).reduce((sum, item) => sum + item.overspentAmount, 0),
    [suggestion]
  );

  const totalCovered = useMemo(
    () => editedCoverages.reduce(
      (sum, coverage) => sum + coverage.transfers.reduce((inner, transfer) => inner + transfer.amount, 0),
      0
    ),
    [editedCoverages]
  );

  if (!isVisible) return null;

  const formatAmount = (amountCents: number) => formatBudgetCurrency(amountCents, currency);

  const updateTransfer = (
    coverageIndex: number,
    transferIndex: number,
    patch: Partial<OverspendTransfer>
  ) => {
    setEditedCoverages(prev => prev.map((coverage, idx) => {
      if (idx !== coverageIndex) return coverage;
      return {
        ...coverage,
        transfers: coverage.transfers.map((transfer, tIdx) => (
          tIdx === transferIndex ? { ...transfer, ...patch } : transfer
        ))
      };
    }));
  };

  const addTransfer = (coverageIndex: number) => {
    const defaultSource = availableSources.find(
      source => getRemainingAmount(source, usedBySource) > 0 && !source.isProtected
    ) || availableSources.find(source => getRemainingAmount(source, usedBySource) > 0);

    if (!defaultSource) return;

    setEditedCoverages(prev => prev.map((coverage, idx) => {
      if (idx !== coverageIndex) return coverage;
      return {
        ...coverage,
        transfers: [
          ...coverage.transfers,
          {
            fromCategoryId: defaultSource.categoryId,
            fromCategoryName: defaultSource.categoryName,
            amount: 0
          }
        ]
      };
    }));
  };

  const removeTransfer = (coverageIndex: number, transferIndex: number) => {
    setEditedCoverages(prev => prev.map((coverage, idx) => {
      if (idx !== coverageIndex) return coverage;
      return {
        ...coverage,
        transfers: coverage.transfers.filter((_, tIdx) => tIdx !== transferIndex)
      };
    }));
  };

  const formatApiError = (error: unknown) => {
    if (isRateLimitError(error)) {
      return t('rateLimitExceeded', { ns: 'overspend' });
    }
    return error instanceof Error ? error.message : t('errorGeneric', { ns: 'overspend' });
  };

  const handleDismiss = async () => {
    if (!suggestion) {
      onClose();
      return;
    }

    setIsDismissing(true);
    try {
      await dismissOverspendCoverage({
        server,
        budgetId,
        budgetPassword,
        month: suggestion.month
      });
    } catch (error) {
      console.warn('Failed to dismiss overspend suggestion:', error);
    } finally {
      setIsDismissing(false);
      onClose();
    }
  };

  const handleReprompt = async () => {
    if (!suggestion || !userFeedback.trim()) return;

    setIsReprompting(true);
    setRepromptError(null);
    setManualMode(false);

    try {
      const updated = await repromptOverspendCoverage({
        server,
        budgetId,
        budgetPassword,
        month: suggestion.month,
        userFeedback: userFeedback.trim()
      });
      onSuggestionChange?.(updated);
      setUserFeedback('');
    } catch (error) {
      setRepromptError(formatApiError(error));
    } finally {
      setIsReprompting(false);
    }
  };

  const handleApply = async () => {
    if (!suggestion) return;
    setIsApplying(true);
    setApplyError(null);

    try {
      const transfers = toApplyTransfers(editedCoverages);
      if (transfers.length === 0) {
        setApplyError(t('noSourcesAvailable', { ns: 'overspend' }));
        return;
      }

      const result = await applyOverspendCoverage({
        server,
        budgetId,
        budgetPassword,
        month: suggestion.month,
        transfers
      });

      if (!result.success) {
        setApplyError(result.errors.join('\n') || t('errorGeneric', { ns: 'overspend' }));
        return;
      }

      setApplySuccess(true);
    } catch (error) {
      const extraErrors = (error as Error & { errors?: string[] }).errors;
      setApplyError(extraErrors?.length ? extraErrors.join('\n') : formatApiError(error));
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleDismiss}>
      <div className="modal-content overspend-modal" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('title', { ns: 'overspend' })}</h3>
          <button className="modal-close" onClick={onClose} type="button">×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner" />
              <p>{t('loading', { ns: 'overspend' })}</p>
            </div>
          )}

          {!loading && loadError && (
            <div className="error">
              <h4>{t('errorTitle', { ns: 'overspend' })}</h4>
              <p>{loadError}</p>
            </div>
          )}

          {!loading && !loadError && suggestion && !applySuccess && (
            <>
              <p className="overspend-intro">{t('intro', { ns: 'overspend' })}</p>
              <p className="overspend-summary">
                {t('totalToCover', { ns: 'overspend', amount: formatAmount(totalToCover) })}
                {' · '}
                {t('totalCovered', { ns: 'overspend', amount: formatAmount(totalCovered) })}
              </p>
              <p className="overspend-suggestion-type">
                {suggestion.fromCache
                  ? t('cachedSuggestion', { ns: 'overspend' })
                  : suggestion.aiUsed
                    ? t('aiSuggestion', { ns: 'overspend' })
                    : t('fallbackSuggestion', { ns: 'overspend' })}
              </p>

              <div className="overspend-feedback">
                <label htmlFor="overspend-feedback-input">
                  {t('feedbackLabel', { ns: 'overspend' })}
                </label>
                <textarea
                  id="overspend-feedback-input"
                  className="overspend-feedback-input"
                  value={userFeedback}
                  onChange={event => setUserFeedback(event.target.value)}
                  placeholder={t('feedbackPlaceholder', { ns: 'overspend' })}
                  rows={3}
                  disabled={isReprompting}
                />
                <button
                  type="button"
                  className="secondary"
                  onClick={handleReprompt}
                  disabled={isReprompting || !userFeedback.trim()}
                >
                  {isReprompting
                    ? t('reprompting', { ns: 'overspend' })
                    : t('reprompt', { ns: 'overspend' })}
                </button>
                {repromptError && (
                  <p className="overspend-reprompt-error" role="alert">{repromptError}</p>
                )}
              </div>

              <div className="overspend-toolbar">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setManualMode(value => !value)}
                >
                  {manualMode
                    ? t('suggestedMode', { ns: 'overspend' })
                    : t('manualMode', { ns: 'overspend' })}
                </button>
              </div>

              <div className="overspend-coverage-list">
                {editedCoverages.map((coverage, coverageIndex) => {
                  const coveredForCategory = coverage.transfers.reduce(
                    (sum, transfer) => sum + transfer.amount,
                    0
                  );
                  const uncovered = Math.max(0, coverage.overspentAmount - coveredForCategory);

                  return (
                    <section key={coverage.overspentCategoryId} className="overspend-coverage-card">
                      <header>
                        <strong>{coverage.overspentCategoryName}</strong>
                        <span className="overspend-amount-badge">
                          {t('overspentAmount', {
                            ns: 'overspend',
                            amount: formatAmount(coverage.overspentAmount)
                          })}
                        </span>
                      </header>

                      {coverage.explanation && !manualMode && (
                        <p className="overspend-explanation">
                          {t('explanation', { ns: 'overspend' })}: {coverage.explanation}
                        </p>
                      )}

                      {uncovered > 0 && (
                        <p className="overspend-uncovered-warning" role="alert">
                          {t('uncoveredWarning', {
                            ns: 'overspend',
                            amount: formatAmount(uncovered),
                            category: coverage.overspentCategoryName
                          })}
                        </p>
                      )}

                      <div className="overspend-transfers">
                        <h5>{t('sourcesHeading', { ns: 'overspend' })}</h5>
                        {coverage.transfers.length === 0 && (
                          <p>{t('noSourcesAvailable', { ns: 'overspend' })}</p>
                        )}

                        {coverage.transfers.map((transfer, transferIndex) => {
                          const source = availableSources.find(
                            item => item.categoryId === transfer.fromCategoryId
                          );
                          const remaining = source
                            ? getRemainingAmount(source, usedBySource)
                            : 0;

                          return (
                            <div key={`${transfer.fromCategoryId}-${transferIndex}`} className="overspend-transfer-row">
                              {manualMode ? (
                                <>
                                  <label>
                                    {t('fromCategory', { ns: 'overspend' })}
                                    <select
                                      value={transfer.fromCategoryId}
                                      onChange={event => {
                                        const nextSource = availableSources.find(
                                          item => item.categoryId === event.target.value
                                        );
                                        updateTransfer(coverageIndex, transferIndex, {
                                          fromCategoryId: event.target.value,
                                          fromCategoryName: nextSource?.categoryName
                                        });
                                      }}
                                    >
                                      <option value="">{t('selectSource', { ns: 'overspend' })}</option>
                                      {availableSources.map(item => (
                                        <option key={item.categoryId} value={item.categoryId}>
                                          {item.categoryName}
                                          {item.isProtected ? ` (${t('protectedBadge', { ns: 'overspend' })})` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label>
                                    {t('amount', { ns: 'overspend' })}
                                    <input
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      value={(transfer.amount / 100).toFixed(2)}
                                      onChange={event => {
                                        const dollars = parseFloat(event.target.value);
                                        updateTransfer(coverageIndex, transferIndex, {
                                          amount: Number.isFinite(dollars)
                                            ? Math.round(dollars * 100)
                                            : 0
                                        });
                                      }}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="remove-button"
                                    onClick={() => removeTransfer(coverageIndex, transferIndex)}
                                  >
                                    {t('removeTransfer', { ns: 'overspend' })}
                                  </button>
                                </>
                              ) : (
                                <div className="overspend-transfer-summary">
                                  <span>
                                    <strong>{transfer.fromCategoryName || source?.categoryName}</strong>
                                    {source?.isProtected && (
                                      <span className="protected-badge">
                                        {t('protectedBadge', { ns: 'overspend' })}
                                      </span>
                                    )}
                                  </span>
                                  <span>
                                    {t('pullAmount', {
                                      ns: 'overspend',
                                      amount: formatAmount(transfer.amount)
                                    })}
                                  </span>
                                  {source && (
                                    <span className="remaining-amount">
                                      {t('remainingAfter', {
                                        ns: 'overspend',
                                        amount: formatAmount(remaining)
                                      })}
                                    </span>
                                  )}
                                  {transfer.reason && (
                                    <small className="transfer-reason">{transfer.reason}</small>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {manualMode && (
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => addTransfer(coverageIndex)}
                          >
                            {t('addTransfer', { ns: 'overspend' })}
                          </button>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>

              {applyError && (
                <div className="error">
                  <h4>{t('errorTitle', { ns: 'overspend' })}</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{applyError}</p>
                </div>
              )}

              <div className="step-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleDismiss}
                  disabled={isDismissing || isApplying}
                >
                  {isDismissing ? t('dismissing', { ns: 'overspend' }) : t('skip', { ns: 'overspend' })}
                </button>
                <button
                  type="button"
                  className="import-execute-button"
                  onClick={handleApply}
                  disabled={isApplying || totalCovered <= 0}
                >
                  {isApplying ? t('applying', { ns: 'overspend' }) : t('confirm', { ns: 'overspend' })}
                </button>
              </div>
            </>
          )}

          {applySuccess && (
            <div className="import-result">
              <h5>{t('successTitle', { ns: 'overspend' })}</h5>
              <p>
                {t('successMessage', {
                  ns: 'overspend',
                  count: toApplyTransfers(editedCoverages).length
                })}
              </p>
              <div className="step-actions">
                <button type="button" className="close-modal-button secondary" onClick={onClose}>
                  {t('close', { ns: 'overspend' })}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverspendCoverageModal;
